/**
 * Book a session: choose a mentor, a length, a slot, then confirm.
 *
 * *** EVERY RULE IS THE SERVER'S ***
 * Which mentors a student may see (their category), which slots exist, what
 * it costs, and whether the booking is allowed at all are all resolved
 * server-side. This screen collects three choices and shows what comes back.
 *
 * *** THE SLOT IS SENT AS A START TIME ONLY ***
 * No duration, deliberately: the server regenerates the slot from the start
 * and derives the rest. A client-supplied duration would let a crafted
 * request book a long session at a short session's price.
 *
 * *** AND THE QUOTE IS NOT THE PRICE ***
 * `create_booking` recomputes and snapshots it. What is shown here is
 * advisory, which is why the confirm step says "you will be charged" rather
 * than promising a figure.
 *
 * *** ONE STEP AT A TIME, WITH A SUMMARY BESIDE IT ***
 * The steps used to stack down the page. Three weeks of calendar is taller
 * than a screen, so choosing a day pushed the time grid below the fold with
 * nothing saying to scroll (reported 2026-10-31).
 *
 * Showing one step fixed that and left the screen mostly empty, so the
 * summary rail carries the other half of the job: what has been chosen so
 * far, what it costs, and the Confirm button — present from the first frame
 * rather than appearing only at the end.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, PageStack, Card } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import {
  useAvailableDates,
  useCreateBooking,
  useMentorSlots,
  usePayForBooking,
  usePriceQuote,
  usePublicMentors,
  usePublicSessionTypes,
} from '@/lib/queries'
import { formatDuration, formatMoney } from '@/lib/format'
import type { MyBooking, PublicMentor, Slot } from '@/types/api'
import styles from './BookSessionPage.module.css'

type StepId = 'mentor' | 'length' | 'day' | 'time'

/* ------------------------------------------------------------- helpers */

/**
 * A local Date from a bare YYYY-MM-DD.
 *
 * *** NOT `new Date(iso)` ***
 * That parses a date-only string as UTC midnight, so anyone west of
 * Greenwich gets the previous day — every weekday name on the strip would
 * be wrong by one.
 */
function localDate(iso: string): Date | null {
  const parts = iso.split('-').map(Number)
  const [y, m, d] = parts
  if (parts.length !== 3 || y === undefined || m === undefined || d === undefined) {
    return null
  }
  return new Date(y, m - 1, d)
}

/** YYYY-MM-DD for a Date, in the viewer's own zone. */
function isoOf(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Initials for the avatar slot when a mentor has no photo. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const first = words[0]?.[0] ?? ''
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || '?'
}

/** A slot's start, as a plain clock time. */
function clock(utc: string): string {
  return new Date(utc).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Morning / Afternoon / Evening.
 *
 * *** THE VIEWER'S OWN CLOCK, NOT THE MENTOR'S ***
 * `mentor_local_start` would group a 9pm session under "Morning" for a
 * student in another zone. The heading has to match the time printed on the
 * button, and that is the local one.
 */
function partOfDay(utc: string): 'Morning' | 'Afternoon' | 'Evening' {
  const h = new Date(utc).getHours()
  if (h < 12) return 'Morning'
  if (h < 17) return 'Afternoon'
  return 'Evening'
}

/**
 * The next `days` calendar days, grouped into rows of seven.
 *
 * *** THE WHOLE WEEK, INCLUDING DAYS WITH NOTHING ***
 * Rendering only open days collapses the grid and "next Tuesday" lands in
 * a different column each week, which defeats the point of a calendar. The
 * closed days are present but inert.
 */
function buildWeeks(days: number): Array<Array<{ iso: string; date: Date }>> {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const cells: Array<{ iso: string; date: Date }> = []
  for (let i = 0; i < days; i += 1) {
    const date = new Date(today)
    date.setDate(today.getDate() + i)
    cells.push({ iso: isoOf(date), date })
  }

  const weeks: Array<Array<{ iso: string; date: Date }>> = []
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7))
  }
  return weeks
}

/* --------------------------------------------------------------- icons */

/* Inline rather than from a sprite: three 16px glyphs used in one place. */

function IconUser() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  )
}

function IconClock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}

/**
 * One line of the summary rail.
 *
 * *** A BUTTON ONLY ONCE IT IS ANSWERED ***
 * Clicking "Mentor — Rohit Meta" to go back and change it is the obvious
 * gesture. Clicking "Time — not chosen yet" is not: that step may not be
 * reachable, and a control that sometimes does nothing is worse than no
 * control. Unanswered lines render as plain divs.
 */
function SummaryRow({
  icon,
  label,
  value,
  sub,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  value: string | null
  sub?: string | null
  onClick?: () => void
}) {
  const set = Boolean(value)
  const body = (
    <>
      <span className={styles.rowIcon}>{icon}</span>
      <span className={styles.rowText}>
        <span className={styles.rowLabel}>{label}</span>
        <span className={styles.rowValue}>{value ?? 'Not chosen yet'}</span>
        {set && sub && <span className={styles.rowSub}>{sub}</span>}
      </span>
    </>
  )

  const className = `${styles.row} ${set ? styles.rowSet : ''}`

  if (set && onClick) {
    return (
      <button
        type="button"
        className={`${className} ${styles.rowBtn}`}
        onClick={onClick}
        aria-label={`${label}: ${value}. Change`}
      >
        {body}
      </button>
    )
  }
  return <div className={className}>{body}</div>
}

/* ----------------------------------------------------------------- page */

export function BookSessionPage() {
  const navigate = useNavigate()
  const toast = useToast()

  const [mentor, setMentor] = useState<PublicMentor | null>(null)
  const [sessionTypeId, setSessionTypeId] = useState<string | null>(null)
  const [day, setDay] = useState<string | null>(null)
  const [slot, setSlot] = useState<Slot | null>(null)
  const [step, setStep] = useState<StepId>('mentor')

  const mentors = usePublicMentors()
  const sessionTypes = usePublicSessionTypes()

  const dates = useAvailableDates(
    mentor?.mentor_id,
    sessionTypeId ?? undefined,
  )
  const slots = useMentorSlots(
    mentor?.mentor_id,
    sessionTypeId ?? undefined,
    day,
  )
  const quote = usePriceQuote(mentor?.mentor_id, sessionTypeId ?? undefined)
  const create = useCreateBooking()
  const pay = usePayForBooking()

  /*
    One length usually exists, and making someone pick from a list of one is
    a step with no decision in it.
  */
  const types = sessionTypes.data?.session_types ?? []
  useEffect(() => {
    const only = types.length === 1 ? types[0] : undefined
    if (!sessionTypeId && only) setSessionTypeId(only.session_type_id)
  }, [types, sessionTypeId])

  const chosenType = types.find((t) => t.session_type_id === sessionTypeId)
  const hasLengthStep = types.length > 1

  /* Three weeks of calendar, whether or not each day has slots. */
  const weeks = useMemo(() => buildWeeks(21), [])
  const todayIso = useMemo(() => isoOf(new Date()), [])

  /* Whether this mentor has ANY opening — the difference between "pick a
     day" and "try another mentor". */
  const openCount = useMemo(
    () => Object.values(dates.data?.dates ?? {}).filter((n) => n > 0).length,
    [dates.data],
  )

  /* Slots split into Morning / Afternoon / Evening, empty groups dropped. */
  const slotGroups = useMemo(() => {
    const order = ['Morning', 'Afternoon', 'Evening'] as const
    const bucket = new Map<string, Slot[]>()
    for (const s of slots.data?.slots ?? []) {
      const key = partOfDay(s.start_utc)
      const list = bucket.get(key)
      if (list) list.push(s)
      else bucket.set(key, [s])
    }
    return order
      .map((name) => ({ name, items: bucket.get(name) ?? [] }))
      .filter((g) => g.items.length > 0)
  }, [slots.data])

  /*
    *** THE STEP LIST IS DERIVED, NOT HARD-CODED ***
    Length is a step only when the tenant offers more than one, so the rail
    — and therefore Back and Next — has to be built from the data. A fixed
    four-step rail would show a step that can never be opened.
  */
  const steps = useMemo(() => {
    const all: Array<{
      id: StepId
      label: string
      done: boolean
      ready: boolean
    }> = [
      { id: 'mentor', label: 'Mentor', done: Boolean(mentor), ready: true },
      {
        id: 'length',
        label: 'Length',
        done: Boolean(sessionTypeId),
        ready: Boolean(mentor),
      },
      {
        id: 'day',
        label: 'Day',
        done: Boolean(day),
        ready: Boolean(mentor && sessionTypeId),
      },
      {
        id: 'time',
        label: 'Time',
        done: Boolean(slot),
        ready: Boolean(mentor && sessionTypeId && day),
      },
    ]
    return hasLengthStep ? all : all.filter((s) => s.id !== 'length')
  }, [mentor, sessionTypeId, day, slot, hasLengthStep])

  const current = Math.max(
    0,
    steps.findIndex((s) => s.id === step),
  )
  const prev = steps[current - 1]
  const next = steps[current + 1]

  /*
    *** A CHOICE HIGHER UP INVALIDATES THE ONES BELOW ***
    …which can leave the open step pointing at one that is no longer
    reachable — change the mentor while standing on Time and that panel has
    nothing to show. This walks back to the last step that IS ready rather
    than rendering an empty one.
  */
  useEffect(() => {
    const here = steps.find((s) => s.id === step)
    if (here && !here.ready) {
      const fallback = [...steps].reverse().find((s) => s.ready)
      if (fallback) setStep(fallback.id)
    }
  }, [steps, step])

  /*
    *** AUTO-ADVANCE ***
    Choosing IS the answer to the step's question, so the step is over.
    Leaving the student on the same panel was the original complaint:
    nothing said what to do next. Each of these clears what depended on it.
  */
  const chooseMentor = (m: PublicMentor) => {
    setMentor(m)
    setDay(null)
    setSlot(null)
    // Straight past Length when there is only one to pick.
    setStep(types.length > 1 ? 'length' : 'day')
  }
  const chooseType = (id: string) => {
    setSessionTypeId(id)
    setDay(null)
    setSlot(null)
    setStep('day')
  }
  const chooseDay = (iso: string) => {
    setDay(iso)
    setSlot(null)
    setStep('time')
  }

  /**
   * Send the student to the gateway for a booking that needs paying.
   *
   * *** THE SLOT IS HELD, NOT BOOKED ***
   * So this cannot simply drop them on the sessions list: the hold expires
   * and the booking is cancelled. Either they reach the bank, or they are
   * told plainly what is still owed.
   */
  const payNow = async (booking: MyBooking) => {
    try {
      const intent = await pay.mutateAsync(booking.booking_id)

      if (intent.checkout_mode === 'redirect') {
        const url = intent.checkout_payload.redirect_url
        if (typeof url === 'string' && url) {
          /*
            Same marker as the plan purchase: `/payment-result` is shared
            with the mobile app and otherwise tells a web student to open
            an app they may not have.
          */
          try {
            sessionStorage.setItem('mentor.webCheckout', '1')
          } catch {
            /* Not worth blocking a payment over. */
          }
          window.location.assign(url)
          return
        }
      }

      /*
        *** `none` IS NOT AN ERROR ***
        The server returns it for a double-tap inside its reuse window: the
        order is still open and there is no second checkout to hand out.
        Saying "could not start payment" would be wrong and would invite a
        third tap.
      */
      toast.error(
        intent.checkout_mode === 'none'
          ? 'Your previous payment attempt is still open. Check your sessions in a moment.'
          : 'This institute takes payment another way. Check your sessions for what to do next.',
      )
      navigate('/sessions')
    } catch (caught) {
      /*
        The booking EXISTS and is held. Sending them to the list is right —
        it is where the unpaid session is, and it is the only place they can
        try again from.
      */
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Your session is held but payment could not be started.',
      )
      navigate('/sessions')
    }
  }

  const confirm = async () => {
    if (!mentor || !sessionTypeId || !slot) return
    try {
      const booking = await create.mutateAsync({
        mentor_id: mentor.mentor_id,
        session_type_id: sessionTypeId,
        slot_start_utc: slot.start_utc,
        /*
          *** AN IDEMPOTENCY KEY, NOT A RETRY GUARD ***
          A double-click or a flaky connection must not produce two
          bookings in the same slot. Derived from the choice itself, so a
          genuine retry of the SAME booking carries the same key.
        */
        idempotency_key: `${mentor.mentor_id}:${slot.start_utc}`,
      })

      /*
        *** A PER-SESSION BOOKING IS NOT BOOKED YET ***
        On a SUBSCRIPTION tenant the plan IS the payment and the server
        confirms immediately. On a PER_SESSION tenant the booking lands
        PENDING_PAYMENT and the slot is only HELD — the stale-hold worker
        cancels it if nobody pays, so "Session booked" would be a lie and
        the student would find it gone.
      */
      if (booking.status === 'PENDING_PAYMENT') {
        await payNow(booking)
        return booking
      }

      toast.success('Session booked.')
      navigate('/sessions')
      return booking
    } catch (caught) {
      /*
        The server's own sentence: the slot was taken, the plan has no
        sessions left, payment is required. Each names a different next
        step, and "could not book" names none of them.
      */
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not book this session.',
      )
    }
  }

  /*
    *** A STUDENT WITH NO CATEGORY CANNOT SEE MENTORS ***
    The server answers 404 "Select a category first". That is an action, not
    a failure, so it is not shown as an error.
  */
  const needsCategory =
    mentors.error instanceof ApiError && mentors.error.status === 404

  const noMentors = Boolean(mentors.data && mentors.data.mentors.length === 0)

  /* What the summary rail shows on each line. */
  const dayLong = day
    ? (localDate(day)?.toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }) ?? day)
    : null

  const ready = Boolean(mentor && sessionTypeId && slot)

  return (
    <PageStack>
      <PageHeader
        title="Book a session"
        description="Choose a mentor, then a time that suits you both."
      />

      {/*
        *** THIS USED TO BE A DEAD END ***
        It stated the requirement and offered nothing to act on — and until
        2026-11-02 the portal had no category picker at all, so a student
        who signed up on the web simply could not book. The link is the
        whole point of the message.
      */}
      {needsCategory && (
        <Card>
          <p className={styles.muted}>
            Choose your category before booking. Your institute set these
            up — ask them if you are not sure which fits.
          </p>
          <div className={styles.emptyAction}>
            <Button variant="primary" onClick={() => navigate('/profile')}>
              Choose your category
            </Button>
          </div>
        </Card>
      )}

      {mentors.isLoading && <p className={styles.muted}>Loading mentors…</p>}

      {mentors.error && !needsCategory && (
        <p className={styles.error}>
          {mentors.error instanceof ApiError
            ? mentors.error.message
            : 'Could not load mentors.'}
        </p>
      )}

      {noMentors && (
        <Card>
          <p className={styles.muted}>
            No mentors are available in {mentors.data?.category_name} yet.
          </p>
        </Card>
      )}

      {mentors.data && !noMentors && (
        <div className={styles.layout}>
          <div>
            {/*
              A thin rail of pills. It says where you are; the summary beside
              it says what you chose, so these carry no values of their own.
            */}
            <div className={styles.steps} role="tablist" aria-label="Booking steps">
              {steps.map((s, i) => (
                <div key={s.id} style={{ display: 'contents' }}>
                  {i > 0 && <span className={styles.stepSep} aria-hidden="true" />}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={s.id === step}
                    disabled={!s.ready}
                    onClick={() => setStep(s.id)}
                    className={`${styles.stepBtn} ${
                      s.id === step ? styles.stepOn : ''
                    } ${s.done && s.id !== step ? styles.stepDone : ''}`}
                  >
                    <span className={styles.stepNum} aria-hidden="true">
                      {s.done && s.id !== step ? '✓' : i + 1}
                    </span>
                    {s.label}
                  </button>
                </div>
              ))}
            </div>

            {/* ------------------------------------------------ 1. mentor */}
            {step === 'mentor' && (
              <>
                <h2 className={styles.panelTitle}>
                  Who would you like to work with?
                </h2>
                <p className={styles.panelNote}>
                  {mentors.data.mentors.length}{' '}
                  {mentors.data.mentors.length === 1 ? 'mentor' : 'mentors'} in{' '}
                  {mentors.data.category_name}.
                </p>

                <div className={styles.mentorGrid}>
                  {mentors.data.mentors.map((m) => (
                    <button
                      key={m.mentor_id}
                      type="button"
                      className={`${styles.mentorCard} ${
                        mentor?.mentor_id === m.mentor_id ? styles.selected : ''
                      }`}
                      onClick={() => chooseMentor(m)}
                      aria-pressed={mentor?.mentor_id === m.mentor_id}
                    >
                      <span className={styles.mentorTop}>
                        <span className={styles.avatar} aria-hidden="true">
                          {m.photo_url ? (
                            <img src={m.photo_url} alt="" />
                          ) : (
                            initials(m.full_name)
                          )}
                        </span>
                        <span className={styles.mentorIdent}>
                          <strong className={styles.mentorName}>
                            {m.full_name}
                          </strong>
                          {m.title && (
                            <span className={styles.mentorTitle}>{m.title}</span>
                          )}
                        </span>
                      </span>

                      {/* The mentor's own words — the one thing on this card
                          that helps choose between two similar people. */}
                      {m.bio && <p className={styles.bio}>{m.bio}</p>}

                      {/*
                        A rating with no count behind it is noise — one
                        five-star review is not a five-star mentor.
                      */}
                      {m.rating_avg && m.rating_count > 0 && (
                        <span className={styles.rating}>
                          <span className={styles.star}>★</span> {m.rating_avg}
                          <span className={styles.ratingCount}>
                            ({m.rating_count}{' '}
                            {m.rating_count === 1 ? 'review' : 'reviews'})
                          </span>
                        </span>
                      )}

                      {m.expertise_tags.length > 0 && (
                        <span className={styles.tags}>
                          {m.expertise_tags.slice(0, 3).map((t) => (
                            <Pill key={t} subtle>
                              {t}
                            </Pill>
                          ))}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* ------------------------------------------------ 2. length */}
            {step === 'length' && (
              <>
                <h2 className={styles.panelTitle}>How long do you need?</h2>
                <div className={styles.chips}>
                  {types.map((t) => (
                    <button
                      key={t.session_type_id}
                      type="button"
                      className={`${styles.chip} ${
                        sessionTypeId === t.session_type_id
                          ? styles.selected
                          : ''
                      }`}
                      onClick={() => chooseType(t.session_type_id)}
                      aria-pressed={sessionTypeId === t.session_type_id}
                    >
                      <span className={styles.chipName}>{t.name}</span>
                      <span className={styles.chipMins}>
                        {formatDuration(t.duration_minutes)}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* --------------------------------------------------- 3. day */}
            {step === 'day' && mentor && (
              <>
                <h2 className={styles.panelTitle}>
                  Pick a day with {mentor.full_name}
                </h2>

                {dates.isLoading && (
                  <p className={styles.muted}>Checking their diary…</p>
                )}

                {!dates.isLoading && openCount === 0 && (
                  <p className={styles.muted}>
                    {mentor.full_name} has no open times in the next three
                    weeks. Try another mentor.
                  </p>
                )}

                {!dates.isLoading && openCount > 0 && (
                  <>
                    {/*
                      *** ONLY WHEN IT NARROWS THE CHOICE ***
                      "Open on 21 of the next 21 days" is true and tells the
                      student nothing — every day is already clickable. The
                      line earns its place when some days are closed, or when
                      there are few enough that scanning is work.
                    */}
                    {openCount < 21 && (
                      <p className={styles.panelNote}>
                        {openCount === 1
                          ? 'One day open in the next three weeks.'
                          : `${openCount} days open in the next three weeks.`}
                      </p>
                    )}
                    <div className={styles.weeks}>
                      {weeks.map((week, i) => (
                        /*
                          The label is INSIDE the row, as its first grid
                          column — a heading above each row cost three lines
                          of height and pushed "Later" off the screen.
                        */
                        <div key={week[0]?.iso ?? i} className={styles.dayRow}>
                          {/*
                            "This week" is what someone booking actually
                            thinks; a date range would make them do the
                            arithmetic.
                          */}
                          <span className={styles.weekLabel}>
                            {i === 0
                              ? 'This week'
                              : i === 1
                                ? 'Next week'
                                : 'Later'}
                          </span>
                          {week.map(({ iso, date }) => {
                            const free = dates.data?.dates[iso] ?? 0
                            return (
                              <button
                                key={iso}
                                type="button"
                                className={`${styles.day} ${
                                  day === iso ? styles.selected : ''
                                } ${iso === todayIso ? styles.dayToday : ''}`}
                                onClick={() => chooseDay(iso)}
                                disabled={free === 0}
                                aria-pressed={day === iso}
                              >
                                {/* Number first: it is what the eye scans
                                    for, and the grid puts the weekday
                                    beside it rather than above. */}
                                <span className={styles.dayNum}>
                                  {date.getDate()}
                                </span>
                                <span className={styles.dayName}>
                                  {date.toLocaleDateString(undefined, {
                                    weekday: 'short',
                                  })}
                                </span>
                                <span className={styles.dayFree}>
                                  {free > 0 ? `${free} free` : '—'}
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* -------------------------------------------------- 4. time */}
            {step === 'time' && day && (
              <>
                <h2 className={styles.panelTitle}>{dayLong}</h2>

                {slots.isLoading && (
                  <p className={styles.muted}>Loading times…</p>
                )}

                {slots.data && slots.data.slots.length === 0 && (
                  <p className={styles.muted}>
                    Those times have just been taken. Pick another day.
                  </p>
                )}

                {slotGroups.length > 0 && (
                  <>
                    <p className={styles.panelNote}>
                      {slots.data?.slots.length} times free
                      {slot ? '' : ' — pick one to finish'}.
                    </p>
                    <div className={styles.timeGroups}>
                      {slotGroups.map((group) => (
                        <div key={group.name}>
                          <span className={styles.weekLabel}>{group.name}</span>
                          <div className={styles.times}>
                            {group.items.map((s) => (
                              <button
                                key={s.start_utc}
                                type="button"
                                className={`${styles.time} ${
                                  slot?.start_utc === s.start_utc
                                    ? styles.selected
                                    : ''
                                }`}
                                onClick={() => setSlot(s)}
                                aria-pressed={slot?.start_utc === s.start_utc}
                              >
                                {clock(s.start_utc)}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/*
              *** BACK AND NEXT, EVEN THOUGH CHOOSING ADVANCES ***
              Auto-advance covers the forward path, but someone who opened an
              earlier step to re-read it needs a way out that is not
              re-picking their answer.
            */}
            <div className={styles.nav}>
              <Button
                variant="ghost"
                onClick={() => prev && setStep(prev.id)}
                disabled={!prev}
              >
                Back
              </Button>
              {next && (
                <Button
                  variant="ghost"
                  onClick={() => setStep(next.id)}
                  disabled={!next.ready}
                >
                  Next: {next.label}
                </Button>
              )}
            </div>
          </div>

          {/*
            *** THE SUMMARY IS HALF THE PAGE'S JOB ***
            It fills the space one step left empty, it saves the student
            holding three choices in their head, and it puts Confirm
            somewhere fixed instead of at the end of a sequence.
          */}
          <aside className={styles.summary}>
            <div className={styles.summaryHead}>
              <h2 className={styles.summaryTitle}>Your session</h2>
              {chosenType && (
                <Pill tone="info" subtle>
                  {chosenType.name}
                </Pill>
              )}
            </div>

            <div className={styles.rows}>
              <SummaryRow
                icon={<IconUser />}
                label="Mentor"
                value={mentor?.full_name ?? null}
                sub={mentor?.title}
                onClick={() => setStep('mentor')}
              />
              <SummaryRow
                icon={<IconCalendar />}
                label="Date"
                value={dayLong}
                onClick={() => setStep('day')}
              />
              <SummaryRow
                icon={<IconClock />}
                label="Time"
                value={slot ? clock(slot.start_utc) : null}
                /*
                  *** THE MENTOR'S OWN TIME ***
                  Shown even when the zones match: the slot was published in
                  the mentor's working day, and seeing it confirms the
                  student read the right hour.
                */
                sub={
                  slot
                    ? `${formatDuration(slot.duration_minutes)} · ${slot.mentor_local_start} for the mentor (${slot.mentor_timezone})`
                    : null
                }
                onClick={() => setStep('time')}
              />
            </div>

            <div className={styles.summaryFoot}>
              {quote.isLoading && (
                <p className={styles.muted}>Checking the price…</p>
              )}

              {quote.data && (
                <>
                  <div className={styles.priceRow}>
                    <span className={styles.priceLabel}>You pay</span>
                    <strong className={styles.priceAmount}>
                      {formatMoney(quote.data.total_amount, quote.data.currency)}
                    </strong>
                  </div>
                  {quote.data.line_items.length > 1 && (
                    <ul className={styles.lineItems}>
                      {quote.data.line_items.map((li, i) => (
                        <li key={`${li.label}-${i}`}>
                          <span>{li.label}</span>
                          <span>{formatMoney(li.amount, quote.data!.currency)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              {/*
                A quote failure is NOT a reason to block booking: a student
                on a subscription has no per-session price at all, and the
                server decides the charge regardless. Said as good news,
                because that is what it is.
              */}
              {quote.error && (
                <span className={styles.covered}>
                  ✓ Your plan covers this session
                </span>
              )}

              {/*
                Disabled until every choice is made, and the label says which
                one is missing — a greyed button with no explanation is the
                failure to avoid.
              */}
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={confirm}
                loading={create.isPending || pay.isPending}
                disabled={!ready}
              >
                {/*
                  *** "CONFIRM" PROMISES SOMETHING PAYMENT DOES NOT ***
                  On a per-session tenant this button holds the slot and
                  sends the student to the bank. Calling it Confirm would
                  have them believe the session was theirs while the hold
                  quietly expired.

                  `quote.data` is the per-session price; a plan-covered
                  booking has no quote and really is confirmed on the spot.
                */}
                {ready
                  ? quote.data
                    ? `Pay ${formatMoney(quote.data.total_amount, quote.data.currency)}`
                    : 'Confirm booking'
                  : !mentor
                    ? 'Pick a mentor to continue'
                    : !day
                      ? 'Pick a day to continue'
                      : 'Pick a time to continue'}
              </Button>
            </div>
          </aside>
        </div>
      )}
    </PageStack>
  )
}
